import { get } from 'lodash';
import React, { useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import classNames from 'classnames';
import { AtlassianNavigation } from '@atlaskit/atlassian-navigation';
import Popup from '@atlaskit/popup';
import ToolTip from '@atlaskit/tooltip';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import AddIcon from '@atlaskit/icon/glyph/add';
import MarketplaceIcon from '@atlaskit/icon/glyph/marketplace';
import InviteTeamIcon from '@atlaskit/icon/glyph/invite-team';
import QuoteIcon from '@atlaskit/icon/glyph/quote';
import { AtlassianIcon } from '@atlaskit/logo';
import { B300 } from '@atlaskit/theme/colors';
import { Inline, Stack } from '@atlaskit/primitives';
import Button from '@atlaskit/button/standard-button';
import {
  AnalyticsTrackAction,
  AnalyticsTrackSubject,
  sendAnalyticsEvent,
  trackSearchEvent,
} from '@atlassian/temp-analytics';
import { useFeatureToggle, useSidebar, withApollo, useAddCustomerModal } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';
import { routePaths, COMMON_SIDEBARS, ENABLE_DESIGN_ITERATION_1, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import {
  SearchData,
  SearchForm,
  SearchInputContainer,
  searchContextKey,
  searchFieldKey,
} from '@css-xp/ppc-search-form';
import { usePartnerCommonSidebars } from '@css-xp/ppc-custom-hooks';
import { AdvocateAccessWall, ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE } from '@css-xp/ppc-access-control';
import { getRouteUrl, getSearchParams } from '@css-xp/ppc-utils';
import { SelfHelp } from '@css-xp/ppc-in-product-help';
import { AppSwitcher } from './components/partner-app-switcher';
import { ProfileButton } from './components/profile-button';
import './navigation-bar.scss';
import { NavigationButton } from './navigation-button';
import { TransactionAccountSwitcher } from './transaction-account-switcher';
import { AppSettingsMenu } from './components/settings-button';
import { getPartnerServiceDeskUrlForNewQuoteHelp } from './utils/partner-service-desk-utils';

export const PartnerCentralProductHome = ({
  txaId,
  redirectToUrl,
  isDesignIteration1Enabled,
}: {
  txaId?: string;
  redirectToUrl?: string;
  isDesignIteration1Enabled?: boolean;
}) => {
  const url = getRouteUrl(txaId ? routePaths.home : routePaths.unauthorized, { txaId: txaId ?? '' });
  const linkClass = classNames('navigation-bar-logo', {
    'navigation-bar-logo-v2': isDesignIteration1Enabled,
  });

  return (
    <>
      <ToolTip content={<>Partner Purchasing Center</>} position="bottom-start" tag="span">
        <Link to={redirectToUrl ?? url} data-testId="ppc-nav-logo" className={linkClass}>
          <div className="navigation-bar-atlassian-icon">
            <AtlassianIcon iconColor={B300} size="small" />
          </div>
          <p className="navigation-bar-ppc-heading">Partner Purchasing Center</p>
        </Link>
      </ToolTip>
    </>
  );
};

interface NavigationBarProps {
  NotificationComponent: React.ComponentType;
}

const CreateButton = ({
  txaId,
  openAddCustomerModal,
}: {
  txaId: string;
  openAddCustomerModal: (payload: any) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEnabled] = useFeatureToggle();
  const { openSidebar } = useSidebar();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const newOrderURL = isDesignIteration1Enabled
    ? getRouteUrl(routePaths.cart, { txaId: txaId })
    : getRouteUrl(routePaths.order, { txaId: txaId });
  const iconLabel = 'create menu';
  const requestQuoteLink = getPartnerServiceDeskUrlForNewQuoteHelp();

  // menu items labels and descriptions
  const CUSTOMER_MENU_ITEM_LABEL = 'Add customer';
  const CUSTOMER_MENU_ITEM_DESCRIPTION = 'Add new company details';
  const ORDER_MENU_ITEM_LABEL = 'Monthly order or estimate';
  const ORDER_MENU_ITEM_DESCRIPTION = 'Buy, change, or transfer monthly subscriptions';
  const QUOTE_MENU_ITEM_LABEL = 'Request an annual quote';
  const QUOTE_MENU_ITEM_DESCRIPTION = 'Transfer, change, or upgrade to annual subscriptions';

  const onAddCustomerClick = () => {
    if (isDesignIteration1Enabled) {
      openAddCustomerModal({
        transactionAccountId: txaId,
      });
      return;
    }

    openSidebar({
      sidebarType: COMMON_SIDEBARS.ADD_CUSTOMER,
      payload: {
        transactionAccountId: txaId,
        hideAddFromBillToAddress: true,
        contentString: {
          HEADING: 'Add customer',
          SUBMIT_BUTTON: 'Add customer',
          NOTIFICATION_SUCCESS_HEADING: 'Customer added',
        },
        onSubmitComplete: () =>
          sendAnalyticsEvent(AnalyticsTrackAction.SUCCESSFUL, AnalyticsTrackSubject.NEW_CUSTOMER, {
            transactionAccountId: txaId,
          }),
        onSubmitFailure: () =>
          sendAnalyticsEvent(AnalyticsTrackAction.FAILED, AnalyticsTrackSubject.NEW_CUSTOMER, {
            transactionAccountId: txaId,
          }),
        beforeSubmit: () =>
          sendAnalyticsEvent(AnalyticsTrackAction.ADDED, AnalyticsTrackSubject.NEW_CUSTOMER, {
            transactionAccountId: txaId,
          }),
      },
      sidebarOptions: {
        isOverlay: true,
        sidebarWidth: '600px',
      },
    });

    // Close the pop-up
    setIsOpen(false);
  };

  const handleOrderClick = () => {
    // When on the new order page, clicking on the button does not open the modal again if closed once.
    // Hence, as a workaround, we are triggering the click event in DOM to open the modal.
    const triggerButton = document.getElementById('ppc-order-page-header-customer-name');

    if (triggerButton) {
      triggerButton.click();
    }

    // Close the pop-up
    setIsOpen(false);
  };

  return (
    <Popup
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
      content={() => (
        <ul className="navigation-bar-create-popup pl-0 pv-8">
          <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.CUSTOMER}>
            <li>
              <div
                onClick={onAddCustomerClick}
                className="navigation-bar-create-item"
                data-testId="nav-create-customer"
              >
                <Inline key={'space.100'} space={'space.100'}>
                  <InviteTeamIcon label="add customer" size="medium" />
                  <Stack>
                    <h4>{CUSTOMER_MENU_ITEM_LABEL}</h4>
                    <p className="navigation-bar-create-popup-text-content">{CUSTOMER_MENU_ITEM_DESCRIPTION}</p>
                  </Stack>
                </Inline>
              </div>
            </li>
          </AdvocateAccessWall>
          <li onClick={handleOrderClick}>
            <Link to={newOrderURL} className="navigation-bar-create-item" data-testId="nav-order-link">
              <Inline key={'space.100'} space={'space.100'}>
                <MarketplaceIcon label={ORDER_MENU_ITEM_LABEL} size="medium" />
                <Stack>
                  <h4>{ORDER_MENU_ITEM_LABEL}</h4>
                  <p className="navigation-bar-create-popup-text-content">{ORDER_MENU_ITEM_DESCRIPTION}</p>
                </Stack>
              </Inline>
            </Link>
          </li>
          <li>
            <div
              className="navigation-bar-create-item"
              data-testId="nav-new-quote-link"
              onClick={() => {
                window.open(requestQuoteLink, '_blank');
              }}
            >
              <Inline key={'space.100'} space={'space.100'}>
                <QuoteIcon label="Request an annual quote" size="medium" />
                <Stack>
                  <h4>{QUOTE_MENU_ITEM_LABEL}</h4>
                  <p className="navigation-bar-create-popup-text-content">{QUOTE_MENU_ITEM_DESCRIPTION}</p>
                </Stack>
              </Inline>
            </div>
          </li>
        </ul>
      )}
      trigger={(triggerProps) => (
        <div className="flex align-center navigation-bar-create-btn">
          <Button
            {...triggerProps}
            appearance="primary"
            isSelected={isOpen}
            onClick={() => setIsOpen(!isOpen)}
            testId="nav-create-btn"
            iconAfter={isDesignIteration1Enabled || <ChevronDownIcon label={iconLabel} size="medium" />}
            iconBefore={isDesignIteration1Enabled && <AddIcon label={iconLabel} size="small" />}
          >
            Create
          </Button>
        </div>
      )}
    />
  );
};

const NavigationBarComponent = ({ NotificationComponent }: NavigationBarProps) => {
  const userProfile = useApplicationContext();
  const [isEnabled] = useFeatureToggle();
  const history = useHistory();
  usePartnerCommonSidebars();
  const [selectedPartnerAccount, setSelectedPartnerAccount] = useLocalStorage(
    `${userProfile?.atlassianAccountId}-selectedTxa`,
    ''
  );
  const partnerAccounts = get(userProfile, 'partnerAccounts', []);
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const { openAddCustomerModal } = useAddCustomerModal();

  const navigationPrimaryItems = useMemo(() => {
    const navigations = [
      {
        name: 'Customers',
        testId: 'address-nav',
        href: getRouteUrl(routePaths.customerAddresses, { txaId: selectedPartnerAccount }),
        exact: true,
      },
      {
        name: 'Invoices',
        testId: 'invoices-nav',
        href: getRouteUrl(routePaths.invoices, { txaId: selectedPartnerAccount }),
        exact: true,
      },
      {
        name: 'Subscriptions',
        testId: 'subscriptions-nav',
        href: getRouteUrl(routePaths.subscriptions, { txaId: selectedPartnerAccount }),
        exact: false,
      },
      {
        name: 'Catalog',
        testId: 'catalog-nav',
        href: getRouteUrl(routePaths.catalog, { txaId: selectedPartnerAccount }),
        exact: true,
      },
    ];
    navigations.unshift({
      name: 'Home',
      testId: 'home-nav',
      href: getRouteUrl(routePaths.home, { txaId: selectedPartnerAccount }),
      exact: true,
    });

    navigations.push({
      name: 'Quotes',
      testId: 'quotes-nav',
      href: getRouteUrl(routePaths.quotes, { txaId: selectedPartnerAccount }),
      exact: true,
    });

    return navigations;
  }, [selectedPartnerAccount, isEnabled]);

  const getNavigationPrimaryItems = () => {
    if (partnerAccounts.length > 0) {
      return [
        <TransactionAccountSwitcher
          key="transaction-account-switcher"
          partnerAccounts={partnerAccounts}
          selectedPartnerAccount={selectedPartnerAccount}
          setSelectedPartnerAccount={setSelectedPartnerAccount}
        />,
        ...navigationPrimaryItems.map((navItem) => (
          <NavigationButton key={navItem.testId} href={navItem.href} testId={navItem.testId} exact={navItem.exact}>
            {navItem.name}
          </NavigationButton>
        )),
      ];
    }
    return [];
  };

  const onSearchSubmit = (data: SearchData) => {
    // encode it to pass special chars like "#" in url
    const query = encodeURIComponent(data[searchFieldKey]);
    history.push(
      `${getRouteUrl(routePaths.search, {
        txaId: selectedPartnerAccount,
      })}?${searchFieldKey}=${query}&${searchContextKey}=ALL`,
      isDesignIteration1Enabled && { txaId: selectedPartnerAccount }
    );
    trackSearchEvent({}, 'global');
  };

  const onSearchReset = () => {
    if (getSearchParams(searchFieldKey) !== null) {
      onSearchSubmit({ [searchFieldKey]: '', [searchContextKey]: 'ALL' });
    }
  };

  const renderCreate = () =>
    partnerAccounts?.length ? (
      <CreateButton txaId={selectedPartnerAccount} openAddCustomerModal={openAddCustomerModal} />
    ) : null;

  const appSearchBox = () => {
    const searchClass = classNames('navigation-search', {
      'navigation-search-v2': isDesignIteration1Enabled,
    });
    if (partnerAccounts?.length) {
      return (
        <div className={searchClass}>
          <SearchForm onSubmit={onSearchSubmit}>
            {() => (
              <SearchInputContainer
                className="search-input-container-nav-bar"
                searchIconSize={isDesignIteration1Enabled ? 'medium' : 'small'}
                isClearable
                onReset={isDesignIteration1Enabled && onSearchReset}
                defaultValue={getSearchParams('query') || ''}
                showSearchMenu={isDesignIteration1Enabled}
                txaId={selectedPartnerAccount}
                shouldClearSearchInput={isDesignIteration1Enabled ? !getSearchParams('query') : false}
              />
            )}
          </SearchForm>
        </div>
      );
    }
    return null;
  };

  const primaryItems = isDesignIteration1Enabled ? [appSearchBox(), renderCreate()] : getNavigationPrimaryItems();

  return (
    <div
      className={classNames({
        'partner-navigation-v2': isDesignIteration1Enabled,
      })}
    >
      <AtlassianNavigation
        label="site"
        moreLabel="More"
        testId="partner-navigation" // Note: testId is leveraged in CSS code
        primaryItems={primaryItems}
        renderProductHome={() => (
          <PartnerCentralProductHome
            txaId={selectedPartnerAccount}
            isDesignIteration1Enabled={isDesignIteration1Enabled}
          />
        )}
        renderAppSwitcher={() => <AppSwitcher />}
        renderProfile={ProfileButton}
        renderSearch={isDesignIteration1Enabled ? undefined : appSearchBox}
        renderCreate={isDesignIteration1Enabled ? undefined : renderCreate}
        renderNotifications={partnerAccounts.length ? NotificationComponent : undefined}
        renderHelp={partnerAccounts?.length ? SelfHelp : undefined}
        renderSettings={() => (partnerAccounts.length > 0 ? <AppSettingsMenu txaId={selectedPartnerAccount} /> : null)}
      />
    </div>
  );
};

export const NavigationBar = withApollo(NavigationBarComponent);
